export type WarningMessageId =
  | 'age'
  | 'device'
  | 'language'
  | 'treatment'
  | 'psychose'
  | 'p4_highrisk'
  | 'p4_lowerrisk'
  | 'p4_minimalrisk';

type WarningConfig = {
  externalUrl?: string;
};

export const warningConfigs: Record<WarningMessageId, WarningConfig> = {
  age:            {},
  device:         {},
  language:       {},
  treatment:      {},
  psychose:       {},
  p4_highrisk:    { externalUrl: 'https://www.113.nl/' },
  p4_lowerrisk:   { externalUrl: 'https://www.113.nl/' },
  p4_minimalrisk: { externalUrl: 'https://www.113.nl/' },
};

export type QuestionId =
  | 'age'
  | 'device'
  | 'language'
  | 'treatment'
  | 'psychose'
  | 'nods1'
  | 'nods2'
  | 'nods3'
  | 'selfharm_gate'
  | 'p4_attempt'
  | 'p4_plan'
  | 'p4_likelihood'
  | 'p4_protective';

export type QuestionOption = {
  labelKey: string;
  value: number;
};

export type ScreeningOption = QuestionOption & {
  triggersWarning?: WarningMessageId;
};

export type LikelihoodOption = QuestionOption;

export type ScreeningQuestion = {
  id: QuestionId;
  type: 'binary';
  i18nKey: string;
  options: ScreeningOption[];
};

export type LikelihoodQuestion = {
  id: QuestionId;
  type: 'multichoice';
  i18nKey: string;
  options: LikelihoodOption[];
};

export type Question = ScreeningQuestion | LikelihoodQuestion;

// Basic eligibility; treatment/psychosis; NODS-CLiP;
// selfharm_gate: self-harm ideation gate; P4 (conditional on selfharm_gate=1)
export const questions: Question[] = [
  {
    id: 'age',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q1',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0, triggersWarning: 'age' },
    ],
  },
  {
    id: 'device',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q3',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0, triggersWarning: 'device' },
    ],
  },
  {
    id: 'language',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q4',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0, triggersWarning: 'language' },
    ],
  },
  {
    id: 'treatment',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q5',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1, triggersWarning: 'treatment' },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'psychose',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q6',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1, triggersWarning: 'psychose' },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'nods1',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q7',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'nods2',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q8',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'nods3',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q9',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'selfharm_gate',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q10',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  // P4 sub-questions (only shown when selfharm_gate = 1)
  {
    id: 'p4_attempt',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q11',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'p4_plan',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q12',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
  {
    id: 'p4_likelihood',
    type: 'multichoice',
    i18nKey: 'buddy_co_screening.questions.q13',
    options: [
      { labelKey: 'buddy_co_screening.options.not_likely', value: 0 },
      { labelKey: 'buddy_co_screening.options.somewhat_likely', value: 1 },
      { labelKey: 'buddy_co_screening.options.very_likely', value: 2 },
      { labelKey: 'buddy_co_screening.options.almost_certain', value: 3 },
    ],
  },
  {
    id: 'p4_protective',
    type: 'binary',
    i18nKey: 'buddy_co_screening.questions.q14',
    options: [
      { labelKey: 'buddy_co_screening.options.yes', value: 1 },
      { labelKey: 'buddy_co_screening.options.no', value: 0 },
    ],
  },
];

export const NODS_QUESTION_IDS: QuestionId[] = ['nods1', 'nods2', 'nods3'];
export const P4_GATE_QUESTION_ID: QuestionId = 'selfharm_gate';
export const P4_QUESTION_IDS: QuestionId[] = ['p4_attempt', 'p4_plan', 'p4_likelihood', 'p4_protective'];
export const ALWAYS_REQUIRED_IDS: QuestionId[] = ['age', 'device', 'language', 'treatment', 'psychose', 'nods1', 'nods2', 'nods3', 'selfharm_gate'];

export function computeNodsScore(answers: Partial<Record<QuestionId, number>>): number {
  return NODS_QUESTION_IDS.reduce((sum, id) => sum + (answers[id] === 1 ? 1 : 0), 0);
}

export type P4RiskLevel = 'high' | 'lower' | 'minimal' | 'no_risk' | 'incomplete';

export function computeP4RiskLevel(answers: Partial<Record<QuestionId, number>>): P4RiskLevel {
  // 'no_risk' (selfharm_gate=Nee) skips the popup entirely; 'minimal' (selfharm_gate=Ja, low scores) still shows p4_minimalrisk advisory
  if (answers[P4_GATE_QUESTION_ID] !== 1) {
    return 'no_risk';
  }

  const attempt = answers.p4_attempt;
  const plan = answers.p4_plan;
  const likelihood = answers.p4_likelihood;
  const protective = answers.p4_protective;

  if (attempt === undefined || plan === undefined || likelihood === undefined || protective === undefined) {
    return 'incomplete';
  }

  if (plan === 1 || likelihood >= 2 || (likelihood === 1 && protective === 0)) {
    return 'high';
  }
  if (plan === 0 && likelihood < 2 && (attempt === 1 || (likelihood === 1 && protective === 1))) {
    return 'lower';
  }
  return 'minimal';
}

export function isScreeningComplete(answers: Partial<Record<QuestionId, number>>): boolean {
  if (!ALWAYS_REQUIRED_IDS.every((id) => answers[id] !== undefined)) {
    return false;
  }
  if (answers[P4_GATE_QUESTION_ID] === 1) {
    return P4_QUESTION_IDS.every((id) => answers[id] !== undefined);
  }
  return true;
}

export function isEligibleForStudy(answers: Partial<Record<QuestionId, number>>): boolean {
  if (answers.age !== 1 || answers.device !== 1 || answers.language !== 1) {
    return false;
  }
  if (answers.treatment !== 0) {
    return false;
  }
  if (answers.psychose !== 0) {
    return false;
  }
  if (computeNodsScore(answers) < 1) {
    return false;
  }
  const p4Risk = computeP4RiskLevel(answers);
  if (p4Risk === 'incomplete' || p4Risk === 'lower' || p4Risk === 'high') {
    return false;
  }
  return true;
}

export function getWebformValues(
  answers: Partial<Record<QuestionId, number>>,
  consent: boolean,
): Record<string, boolean | number> {
  const values: Record<string, boolean | number> = { consent };
  questions.forEach((q) => {
    const val = answers[q.id];
    if (val !== undefined) {
      values[q.id] = q.id === 'p4_likelihood' ? val : Boolean(val);
    }
  });
  return values;
}
